import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import Footer from './Footer';
import PageMeta from './PageMeta';

/**
 * LegalLayout — shared shell for every static / legal / company page.
 *
 * The site-wide <Navbar /> (rendered by App.js) is the single source of truth
 * for the top header. This layout intentionally does NOT render its own
 * sticky mini-header — that caused a double-header bug on /privacy, /terms,
 * /refund-policy, /cancellation-policy, /contact, /about-us, /why-acf-tourism.
 *
 * What this layout provides:
 *  1. Page-level meta tags via <PageMeta />
 *  2. A unified gradient hero band with breadcrumb + H1 + subtitle
 *  3. A centred white content card with .legal-content typography
 *  4. The shared <Footer />
 *
 * Props
 *  - title:           required, page H1 + document title
 *  - subtitle:        optional, hero sub-paragraph
 *  - lastUpdated:     optional, badge under the hero
 *  - breadcrumbLabel: optional, label between "Home" and current page
 *                     (default: "Legal" for legal pages, override for marketing)
 *  - description:     optional, meta description (defaults to subtitle)
 *  - canonicalPath:   optional, canonical URL path (e.g. "/about-us")
 *  - testId:          optional, data-testid on the page wrapper
 *  - children:        page body content (uses .legal-content prose styles)
 */
const LegalLayout = ({
  title,
  subtitle,
  lastUpdated,
  breadcrumbLabel = 'Legal',
  description,
  canonicalPath,
  testId,
  children,
}) => {
  const slug = title.toLowerCase().replace(/\s+/g, '-');
  return (
    <div
      className="min-h-screen bg-gray-50"
      data-testid={testId || `legal-page-${slug}`}
    >
      <PageMeta
        title={`${title} | ACF Tourism`}
        description={description || subtitle}
        canonicalPath={canonicalPath}
      />

      {/* Hero band — unified gradient + breadcrumb + H1 */}
      <div className="bg-gradient-to-br from-[#1B375B] via-[#2d4d7a] to-[#335E77] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-14">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 text-xs text-white/70 mb-4"
            data-testid="page-breadcrumb"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/85">{breadcrumbLabel}</span>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white" aria-current="page">{title}</span>
          </nav>
          <h1
            className="text-3xl md:text-5xl font-bold mb-3 tracking-tight"
            data-testid="page-title"
          >
            {title}
          </h1>
          {subtitle && (
            <p className="text-white/80 text-base md:text-lg max-w-2xl leading-relaxed">
              {subtitle}
            </p>
          )}
          {lastUpdated && (
            <div className="mt-5 inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full text-xs text-white/90">
              <span className="w-1.5 h-1.5 rounded-full bg-[#659A9B]" />
              Last updated: {lastUpdated}
            </div>
          )}
        </div>
      </div>

      {/* Content card */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-8 pb-16 relative">
        <article className="legal-content bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-10">
          {children}
        </article>
      </div>

      <Footer />
    </div>
  );
};

export default LegalLayout;
